import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { searchMembers } from "@/features/chat/api";
import { useChatStore } from "@/features/chat/store/chat-store";
import type { Member } from "@/features/chat/types";
import { initials } from "@/features/chat/utils";
import { cn } from "@/lib/utils";

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (conversationId: string) => void;
}

function MemberRow({
  member,
  selected,
  onToggle,
}: {
  member: Member;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
        selected ? "bg-accent" : "hover:bg-muted",
      )}
    >
      <Avatar className="size-8 border">
        <AvatarFallback className="text-primary text-xs font-bold">
          {initials(member.name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-medium">{member.name}</p>
        <p className="text-muted-foreground truncate text-xs">{member.email}</p>
      </div>
      {selected && <Check className="text-primary size-4 shrink-0" />}
    </button>
  );
}

export function NewConversationDialog({
  open,
  onOpenChange,
  onCreated,
}: NewConversationDialogProps) {
  const createConversation = useChatStore((s) => s.createConversation);
  const [members, setMembers] = useState<Member[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void searchMembers(query).then((list) => {
      if (!cancelled) setMembers(list);
    });
    return () => {
      cancelled = true;
    };
  }, [open, query]);

  // Reset when the dialog closes.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelected(new Set());
      setGroupName("");
    }
  }, [open]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function create(kind: "direct" | "group") {
    const memberIds = [...selected];
    setBusy(true);
    try {
      const conversation = await createConversation({
        kind,
        name: kind === "group" ? groupName.trim() : "",
        memberIds,
      });
      onCreated(conversation.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't start the conversation.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New conversation</DialogTitle>
          <DialogDescription>Message a teammate or start a group channel.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="direct" onValueChange={() => setSelected(new Set())}>
          <TabsList className="w-full">
            <TabsTrigger value="direct" className="flex-1">
              Direct
            </TabsTrigger>
            <TabsTrigger value="group" className="flex-1">
              Group
            </TabsTrigger>
          </TabsList>

          <Input
            placeholder="Search teammates…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mt-3"
          />
          <div className="mt-2 max-h-64 space-y-0.5 overflow-y-auto">
            {members.length === 0 ? (
              <p className="text-muted-foreground px-3 py-6 text-center text-sm">
                No teammates found.
              </p>
            ) : (
              members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  selected={selected.has(m.id)}
                  onToggle={() => toggle(m.id)}
                />
              ))
            )}
          </div>

          <TabsContent value="direct" className="mt-3">
            <Button
              type="button"
              className="w-full"
              disabled={selected.size !== 1 || busy}
              onClick={() => void create("direct")}
            >
              Start direct message
            </Button>
            {selected.size > 1 && (
              <p className="text-muted-foreground mt-1 text-center text-xs">
                Pick one teammate for a direct message, or use the Group tab.
              </p>
            )}
          </TabsContent>

          <TabsContent value="group" className="mt-3 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="group-name">Group name</Label>
              <Input
                id="group-name"
                placeholder="e.g. OIC Team"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </div>
            <Button
              type="button"
              className="w-full"
              disabled={selected.size < 1 || !groupName.trim() || busy}
              onClick={() => void create("group")}
            >
              Create group ({selected.size})
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
